import React, { useContext, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import { useTranslation } from 'react-i18next';
import * as styles from '../../offices/modals/modal.styles';
import type Office from '../../offices/models/office';
import OfficeListCard from '../../offices/cards/office-list-card.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import Row from '../../../core/components/row/row.component';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import Grid from '../../../core/components/grid/grid.component';
import type Parking from '../models/parking';
import ConfirmationModal from '../../offices/modals/confirmation-modal.component';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly parking: Parking;
  readonly allOffices: Office[];
  readonly handleSave: (originalParking: Parking, changedParking: Parking) => Promise<void>;
  readonly onDelete: (value: Parking) => void;
  readonly parkingIsValid: (value: Parking) => boolean;
}

const EditParkingModal = ({
  showModal,
  handleClose,
  parking,
  allOffices,
  handleSave,
  onDelete,
  parkingIsValid,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [didFillData, setDidFillData] = useState(false);
  const [updateParking, setUpdatedParking] = useState<Parking>({ ...parking });
  const [isSaving, setIsSaving] = useState(false);
  const sortedOffices = allOffices.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const resetModalState = async (): Promise<void> => {
    setDidFillData(false);
  };

  const handleCloseModal = async (): Promise<void> => {
    resetModalState();
    handleClose();
  };

  const initialParking = { ...parking };

  const handleCancelModal = (): void => {
    setUpdatedParking(initialParking);
    handleCloseModal();
  };

  const HandleParkingNameChange = (value: string): void => {
    const parkingCopy = { ...updateParking };
    parkingCopy.name = value;
    setUpdatedParking(parkingCopy);
  };

  const HandleParkingCapacityChange = (value: string): void => {
    const parkingCopy = { ...updateParking };
    if (Number.isFinite(Number(value)) && Number(value) >= 0) {
      parkingCopy.capacity = Number(value);
      setUpdatedParking(parkingCopy);
    }
  };

  const handleDelete = (): void => {
    onDelete(parking);
    handleCloseModal();
  };

  const handleUpdate = async (): Promise<void> => {
    if (isSaving) {
      return;
    }

    if (parkingIsValid(updateParking)) {
      setIsSaving(true);
      await handleSave(parking, updateParking);
      setIsSaving(false);
      handleClose();
    }
  };

  const handleOfficeSelected = (selectedOffice: string): void => {
    const parkingCopy = { ...updateParking };
    const parkingOffices = [...parkingCopy.officeIds];

    if (parkingOffices.includes(selectedOffice)) {
      parkingOffices.splice(parkingOffices.indexOf(selectedOffice), 1);
    } else {
      parkingOffices.push(selectedOffice);
    }

    parkingCopy.officeIds = parkingOffices;

    setUpdatedParking(parkingCopy);
  };

  if (showModal && !stringIsNullOrWhitespace(parking.name) && !didFillData) {
    setDidFillData(true);
  }

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCancelModal} style={styles.modalStyle(isMobile, 800)}>
      <Column className={style(styles.modalBody(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader(isMobile, 8))}>
          {t('edit')} {updateParking.name}
        </span>
        <LabeledInput
          labelText={t('parking-name').toString()}
          value={updateParking.name}
          onChange={(value): void => {
            HandleParkingNameChange(value);
          }}
        />
        <LabeledInput
          labelText={t('number-of-parking-spots').toString()}
          value={updateParking.capacity.toString()}
          onChange={(value): void => {
            HandleParkingCapacityChange(value);
          }}
        />
        <span className={style(styles.modalText)}>{t('offices-using-this-parking')}</span>
        {sortedOffices.length === 0 ? (
          <span className={style(styles.emptyListMessage)}>{t('no-offices-available')}</span>
        ) : (
          <Grid
            rowClass={style(styles.gridRow)}
            itemSpacing={16}
            numberOfColumns={3}
            numberOfItems={sortedOffices.length}
            renderItem={(index): React.JSX.Element => {
              const officeItem = sortedOffices[index];
              return (
                officeItem && (
                  <div
                    className={isMobile ? style(styles.rowItem(true)) : undefined}
                    key={officeItem.id}
                    onClick={(): void => {
                      if (officeItem.id === undefined) {
                        return;
                      }

                      handleOfficeSelected(officeItem.id);
                    }}
                    role="presentation"
                  >
                    <OfficeListCard
                      office={officeItem}
                      checked={officeItem.id !== undefined && updateParking.officeIds.includes(officeItem.id)}
                    />
                  </div>
                )
              );
            }}
          />
        )}
        <Row
          className={style(styles.deleteButton)}
          crossAxisAlignment={CrossAxisAlignment.Center}
          onClick={(): void => {
            setShowDeleteModal(true);
          }}
        >
          <span>{t('delete-parking')}</span>
          <DeleteIcon className={style(styles.deleteIcon)} />
        </Row>
        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              onClick={(): void => {
                handleUpdate();
              }}
            >
              {t('save')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCancelModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
      <ConfirmationModal
        showModal={showDeleteModal}
        itemName={updateParking.name}
        handleAccept={handleDelete}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
    </Modal>
  );
};

export default EditParkingModal;
